import React, { useEffect, useState } from "react";
import {
  CloseOutlined,
  CheckSquareOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Modal, Button, Divider, Typography, Space } from "antd";
import EmployeeSalaryWrapper from "./index.style";
import Header from "../../employees/employeeCard/Header";
import Details from "../../employees/employeeCard/Details";
import SalaryTable from "./SalaryTable";
import ApprovalModal from "../../employees/components/ApprovalModal";
import RestrictedComponent from "../../../components/util/RestrictedComponent";
import { loadRecord, reset } from "../../employeeDetails/employeeDetailsSlice";
import { reset as resetScheduling } from "../../employeeScheduling/schedulingSlice";
import { loadSalaryLines, resetSalaryLines } from "../salarySlice";
import ApprovedHeader from "./ApprovedHeader";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import SalaryLineForm from "./SalaryLineForm";
import moment from "moment";
import {
  EmployeeRecord,
  EmployeeSalaryModalProps,
  SalaryState,
  RouteParams,
} from "../types";
import { Employee } from "../../../types/employee";

const { Text } = Typography;
const flatten = require("flat");

// Constants
const MODAL_STYLES = {
  wrapper: { height: "95vh", top: "2.5vh" },
  body: { padding: 0 },
  content: { height: "95vh" },
  header: { height: "40px" },
};

/**
 * Gets the current date based on active year and month, or returns the current date.
 *
 * @param {number} [activeYear] - The year to set
 * @param {number} [activeMonth] - The month to set (1-12)
 * @returns {moment.Moment} A moment object representing the date
 */
const getCurrentDate = (
  activeYear?: number,
  activeMonth?: number
): moment.Moment =>
  activeYear && activeMonth
    ? moment()
        .set("year", activeYear)
        .month(activeMonth - 1)
    : moment();

/**
 * Flattens an employee record for easier data access.
 *
 * @param {EmployeeRecord | null | undefined} record - The employee record to flatten
 * @returns {EmployeeRecord} A flattened employee record
 */
const getEmployeeData = (record?: EmployeeRecord | null): Employee =>
  record ? flatten(record.employee, { safe: true }) : ({} as Employee);

/**
 * Determines the branch ID to use based on admin status and available branch IDs.
 *
 * @param {boolean} isAdmin - Whether the current user is an admin
 * @param {string | number} activeBranch - The active branch ID from the state
 * @param {string | number} [branchId] - Optional branch ID from route params
 * @returns {string | number} The branch ID to use
 */
const getBranchId = (
  isAdmin: boolean,
  activeBranch: number | null,
  branchId?: string | number
): string | number =>
  isAdmin ? (activeBranch === null ? "" : activeBranch) : branchId || "";

/**
 * EmployeeSalaryModal Component
 *
 * A comprehensive modal component for displaying and managing employee salary information.
 * This component serves as a container for salary data, approval workflows, and salary editing
 * capabilities based on user permissions.
 *
 * Features:
 * - Displays employee's personal and salary information
 * - Supports different viewing modes (view/edit) based on permissions
 * - Shows status indicators for salary states (locked, approved, not run)
 * - Integrates with approval workflows through a nested modal
 * - Allows salary recalculation and editing through child components
 * - Responsive layout with employee header and details
 *
 * Usage Notes:
 * - The 'edit' mode requires appropriate permissions through RestrictedComponent
 * - Salary status is displayed differently based on locked/approved states
 * - Integration with Redux for state management
 * - Supports internationalization through the intl context
 *
 * @example
 * ```tsx
 * <EmployeeSalaryModal
 *   visible={modalVisible}
 *   onHide={() => setModalVisible(false)}
 *   record={employeeData}
 *   mode="edit"
 *   onRecalculate={handleRecalculate}
 *   onApprove={handleApprove}
 *   employeeId={123}
 *   isApproved={false}
 *   summary={{ locked: false, salary_run_at: "2023-01-01" }}
 *   fromSalary={true}
 * />
 * ```
 */
const EmployeeSalaryModal: React.FC<EmployeeSalaryModalProps> = ({
  onHide,
  visible,
  record,
  mode,
  onRecalculate,
  onApprove,
  employeeId,
  isApproved,
  summary,
  fromSalary,
}) => {
  const dispatch = useDispatch<any>();
  const intl = useIntl();
  const location = useLocation();
  const { branchId } = useParams<RouteParams>();
  const [approvalModalVisible, setApprovalModalVisible] =
    useState<boolean>(false);

  // Redux state
  const { activeBranch, activeYear, activeMonth } = useSelector<
    any,
    SalaryState
  >((state) => state.salary);
  const isAdmin = location.pathname.includes("admin");

  // Derived data
  const date = getCurrentDate(activeYear, activeMonth);
  const employee = getEmployeeData(record);

  /**
   * Fetch salary lines when the modal becomes visible
   * and the employee record is available
   */
  useEffect(() => {
    if (visible && record?.employee_id) {
      dispatch(loadSalaryLines(isAdmin, record.employee_id));
    }
  }, [dispatch, record, visible, isAdmin]);

  /**
   * Handles closing the modal and resetting salary lines
   */
  const handleClose = (): void => {
    onHide();
    dispatch(resetSalaryLines(null));
  };

  /**
   * Opens the approval modal and loads the employee record
   */
  const launchApprovalModal = (): void => {
    const params = {
      id: employee.id,
      branchId: getBranchId(isAdmin, activeBranch, branchId),
      admin: isAdmin,
    };
    // Use type assertion to bypass TypeScript's type checking
    // @ts-ignore - The implementation accepts params even though the type definition doesn't
    dispatch(loadRecord(params as any));
    setApprovalModalVisible(true);
  };

  /**
   * Closes the approval modal and resets related state
   */
  const hideApprovalModal = (): void => {
    setApprovalModalVisible(false);
    dispatch(resetScheduling(null));
    dispatch(reset(null));
  };

  /**
   * Renders the appropriate status message based on salary state
   *
   * @returns {React.ReactNode} Status message component
   */
  const renderStatusMessage = (): React.ReactNode => {
    if (summary && !summary.locked && !summary.salary_run_at) {
      return (
        <Text ellipsis type="danger">
          {intl.formatMessage({ id: "app.salary.branchSalaryDidNotRun" })}
        </Text>
      );
    }
    if (summary?.locked) {
      return (
        <Space>
          <LockOutlined style={{ color: "#faad14" }} />
          <Text ellipsis type="warning">
            {intl.formatMessage({ id: "app.salary.branchSalaryIsLocked" })}
          </Text>
        </Space>
      );
    }
    if (isApproved) {
      return (
        <Space>
          <CheckSquareOutlined style={{ color: "#52c41a" }} />
          <Text ellipsis type="success">
            {intl.formatMessage({ id: "app.salary.salaryIsApproved" })}
          </Text>
        </Space>
      );
    }
    return null;
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width="90%"
      style={MODAL_STYLES.wrapper}
      styles={{ body: MODAL_STYLES.body }}
      open={visible}
      destroyOnClose
    >
      <div style={MODAL_STYLES.content}>
        <div className="header" style={MODAL_STYLES.header}>
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={handleClose}
          />
        </div>
        <EmployeeSalaryWrapper>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "flex-end",
            }}
          >
            <div style={{ display: "flex" }}>
              <Header record={employee} />
              <div className="spacer" />
              <Details record={employee} />
            </div>
          </div>
          <Divider />
          {renderStatusMessage()}
          {mode === "edit" ? (
            <RestrictedComponent
              resource="branch_salary"
              action="editor"
              comp={null}
            >
              <SalaryLineForm
                onRecalculate={onRecalculate}
                onApprove={onApprove}
                employeeId={employeeId}
                isApproved={isApproved || false}
                record={employee}
                fromSalary={fromSalary}
                onlaunchApprovalModal={launchApprovalModal}
              />
            </RestrictedComponent>
          ) : (
            <ApprovedHeader record={employee} />
          )}
          <SalaryTable mode={mode} employee={employee} date={date} />
        </EmployeeSalaryWrapper>
      </div>
      {fromSalary && (
        <ApprovalModal
          onHide={hideApprovalModal}
          visible={approvalModalVisible}
          initialDate={date}
        />
      )}
    </Modal>
  );
};

export default EmployeeSalaryModal;
